import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import './DashboardVentas.css';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const formatearGuaranies = (valor) => {
  if (valor == null) return 'Gs. 0';
  return 'Gs. ' + Math.round(valor).toLocaleString('es-PY');
};

const formatearEjeY = (valor) => {
  if (valor >= 1000000000) return (valor / 1000000000).toFixed(1) + 'B';
  if (valor >= 1000000) return (valor / 1000000).toFixed(1) + 'M';
  if (valor >= 1000) return (valor / 1000).toFixed(0) + 'K';
  return valor.toString();
};

const obtenerFechaHoy = () => new Date().toISOString().split('T')[0];

const obtenerPrimerDiaMes = () => {
  const hoy = new Date();
  return new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().split('T')[0];
};

const rellenarMesesFaltantes = (datos, fechaHasta) => {
  const fin = new Date(fechaHasta + 'T00:00:00');
  const meses = [];

  for (let i = 11; i >= 0; i--) {
    const fecha = new Date(fin.getFullYear(), fin.getMonth() - i, 1);
    const anio = fecha.getFullYear();
    const mes = fecha.getMonth() + 1;
    const encontrado = datos.find(d => d.anio === anio && d.mes === mes);
    meses.push({
      name: MESES[mes - 1] + ' ' + anio,
      total: encontrado ? encontrado.totalVentas : 0
    });
  }

  return meses;
};

function DashboardVentas() {
  const [fechaDesde, setFechaDesde] = useState(obtenerPrimerDiaMes());
  const [fechaHasta, setFechaHasta] = useState(obtenerFechaHoy());
  const [dashboard, setDashboard] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargarDashboard = async () => {
    setCargando(true);
    try {
      const response = await axios.get(getApiUrl('/api/ventas/dashboard'), {
        params: { fechaDesde, fechaHasta }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error al cargar dashboard de ventas:', error);
      alert('Error al cargar los datos de ventas');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarDashboard();
  }, []);

  const chartData = dashboard
    ? rellenarMesesFaltantes(dashboard.ventasMensuales || [], fechaHasta)
    : [];

  return (
    <div className="dashboard-ventas-container">
      <h2 className="dashboard-ventas-titulo">Dashboard de Ventas</h2>

      <div className="dashboard-filtros">
        <label>Desde:</label>
        <input
          type="date"
          value={fechaDesde}
          onChange={(e) => setFechaDesde(e.target.value)}
        />
        <label>Hasta:</label>
        <input
          type="date"
          value={fechaHasta}
          onChange={(e) => setFechaHasta(e.target.value)}
        />
        <button
          className="btn-consultar"
          onClick={cargarDashboard}
          disabled={cargando}
        >
          {cargando ? 'Cargando...' : 'Consultar'}
        </button>
      </div>

      {cargando && <div className="dashboard-loading">Cargando datos...</div>}

      {!cargando && dashboard && (
        <>
          <div className="dashboard-kpi-grid">
            <div className="dashboard-kpi-card">
              <div className="dashboard-kpi-label">Total Ventas en el Periodo</div>
              <div className="dashboard-kpi-valor">
                {formatearGuaranies(dashboard.totalVentas)}
              </div>
            </div>
          </div>

          <div className="dashboard-grid-2col">
            <div className="dashboard-seccion">
              <h3>Top 5 Clientes</h3>
              {dashboard.topClientes && dashboard.topClientes.length > 0 ? (
                <table className="dashboard-tabla">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Cliente</th>
                      <th>Total Facturado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.topClientes.map((cliente, index) => (
                      <tr key={cliente.idCliente}>
                        <td>{index + 1}</td>
                        <td>{cliente.nomCliente || 'Sin nombre'}</td>
                        <td>{formatearGuaranies(cliente.totalFacturado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="dashboard-sin-datos">Sin datos en el periodo seleccionado</div>
              )}
            </div>

            <div className="dashboard-seccion">
              <h3>Ventas Mensuales (12 meses)</h3>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: '#7f8c8d' }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      tickFormatter={formatearEjeY}
                      tick={{ fontSize: 12, fill: '#7f8c8d' }}
                    />
                    <Tooltip
                      formatter={(value) => [formatearGuaranies(value), 'Ventas']}
                      labelStyle={{ color: '#001A70', fontWeight: 'bold' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke="#2DCCD3"
                      strokeWidth={2}
                      dot={{ r: 5, fill: '#2DCCD3', stroke: '#001A70', strokeWidth: 1 }}
                      activeDot={{ r: 7, fill: '#001A70' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="dashboard-sin-datos">Sin datos para el grafico</div>
              )}
            </div>
          </div>

          <div className="dashboard-seccion">
            <h3>Top 10 Articulos mas Vendidos</h3>
            {dashboard.topArticulos && dashboard.topArticulos.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={dashboard.topArticulos.map(a => ({
                    name: a.descripcion && a.descripcion.length > 25
                      ? a.descripcion.substring(0, 25) + '...'
                      : (a.descripcion || 'Sin descripcion'),
                    total: a.totalFacturado,
                    fullName: a.descripcion
                  }))}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis
                    type="number"
                    tickFormatter={formatearEjeY}
                    tick={{ fontSize: 12, fill: '#7f8c8d' }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={180}
                    tick={{ fontSize: 11, fill: '#333' }}
                  />
                  <Tooltip
                    formatter={(value) => [formatearGuaranies(value), 'Facturado']}
                    labelFormatter={(label, payload) =>
                      payload && payload[0] ? payload[0].payload.fullName : label
                    }
                    labelStyle={{ color: '#001A70', fontWeight: 'bold' }}
                  />
                  <Bar
                    dataKey="total"
                    fill="#001A70"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="dashboard-sin-datos">Sin datos de articulos en el periodo</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default DashboardVentas;
